/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// 2) Third-party library imports
import { createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { filter, get, includes } from 'lodash-es';
import { App } from 'antd';

// 3a) Constants
import { DEFAULT_NUMBER_OF_DAYS_FOR_RECENT_COMMENT } from 'src/constants/common';

// 3d) Utility functions / interfaces
import { DynamicObject, ResponseModalObject } from 'src/utils/commonInterfaces';

// 3e) Redux actions & types
import { CompaniesState, Company, CompanyUserRole, QuestionAnswerFormItem, QuestionAnswerItem, SidebarItem, SystemConfiguration, WordingItem, WorkflowOption } from './types';
import type { RootState } from '../storeTypes';

export const initialState: CompaniesState = {
	loading: false,
	userCompanies: [],
	selectedUserCompany: undefined,
	saveLoading: false,
	systemConfiguration: undefined,
	workflowOptions: [],
	fullWorkflowOptions: [],
	workflowFetchLoading: false,
    customWording: {},
    wordingLoading: false,
    questionAnswerItems: [],
    questionAnswerLoading: false,
    companyStudentMenuItemsLoading: false,
    companyStudentMenuItems: []
};

const companySlice = createSlice({
	name: 'company',
	initialState,
	reducers: {
		getUserCompaniesRequestAction: (state) => {
			state.loading = true;
		},
		getUserCompaniesSuccessAction: (state, action: PayloadAction<CompanyUserRole[]>) => {
			state.loading = false;
			state.userCompanies = action.payload;
		},
		selectUserCompanyRequestAction: {
			reducer: (
				state, 
				_action: PayloadAction<{
					companyUserRoleObject: CompanyUserRole,
					callback?: () => void,
					modal?: ReturnType<typeof App.useApp>['modal'],
				}>
			) => {
				state.loading = true;
			},
			prepare: (
				companyUserRoleObject: CompanyUserRole,
				callback?: () => void,
				modal?: ReturnType<typeof App.useApp>['modal'],
			) => ({
				payload: {
					companyUserRoleObject,
					callback,
					modal
				}
			})
		},
		selectUserCompanySuccessAction: (state, action: PayloadAction<CompanyUserRole>) => {
			state.loading = false;
			state.selectedUserCompany = action.payload;
		},
		saveUserCompanyRequestAction: {
			reducer: (
				state, 
				_action: PayloadAction<{ 
					payload: any,
					callback?: (response: ResponseModalObject) => void
				}>
			) => {
				state.saveLoading = true;
			},
			prepare: (
				payload: any,
				callback?: (response: ResponseModalObject) => void
			) => ({
				payload: {
					payload,
					callback
				}
			})
		},
		saveUserCompanyResponseAction: (state) => {
			state.saveLoading = false;
		},
		uploadCompanyLogoRequestAction: {
			reducer: (
				state,
				_action: PayloadAction<{
					fileToUpload: any,
    				callback: (response: ResponseModalObject) => void
				}>
			) => {
				state.saveLoading = true;
			},
			prepare: (
				fileToUpload: any,
				callback: (response: ResponseModalObject) => void
			) => ({
				payload: {
					fileToUpload,
					callback
				}
			})
		},
		getSystemConfigurationSuccessAction: (state, action: PayloadAction<SystemConfiguration>) => {
			state.systemConfiguration = action.payload;
		},
		getCompanyWorkflowOptionsRequestAction: {
			reducer: (state) => {
				state.workflowFetchLoading = true;
			},
			prepare: (
				companyIds: string[] | undefined = undefined,
				isOrgView: boolean = false
			) => ({
				payload: {
					companyIds,
					isOrgView
				}
			})
		},
		getCompanyWorkflowOptionsSuccessAction: (state, action: PayloadAction<WorkflowOption[]>) => {
			const fullWorkflowOptions: WorkflowOption[] = action.payload;
            const limitedWorkflowTypes = ['Customer', 'Invoice'];
            const limitedWorkflowOptions: WorkflowOption[] = filter(fullWorkflowOptions, opt => includes(limitedWorkflowTypes, opt.Type));

			state.workflowFetchLoading = false;
			state.workflowOptions = limitedWorkflowOptions;
            state.fullWorkflowOptions = fullWorkflowOptions;
		},
		saveCompanyUserSettingsRequestAction: {
			reducer: (
				state, 
				_action: PayloadAction<{ 
					payload: any,
					callback?: (response: ResponseModalObject) => void
				}>
			) => {
				state.saveLoading = true;
			},
			prepare: (
				payload: any,
				callback?: (response: ResponseModalObject) => void
			) => ({
				payload: {
					payload,
					callback
				}
			})
		},
		saveCompanyUserSettingsResponseAction: (state) => {
			state.saveLoading = false;
		},
		getCustomFieldsChangesRequestAction: {
			reducer: (
				state,
				_action: PayloadAction<{
					payload: any,
					callback: (response: ResponseModalObject) => void
				}>
			) => {
				// This action is handled by saga only, no state change needed
			},
			prepare: (
				payload: any,
				callback: (response: ResponseModalObject) => void
			) => ({
				payload: {
					payload,
					callback
				}
			})
		},
        getCompanyCustomWordingRequestAction: (
            state
        ) => {
            state.wordingLoading = true;
        },
        getCompanyCustomWordingResponseAction: (
            state,
            action: PayloadAction<WordingItem>
        ) => {
            state.wordingLoading = false;
            state.customWording = action.payload;
        },
        saveCompanyCustomWordingRequestAction: {
            reducer: (
                state, 
                _action: PayloadAction<{ 
                    customWording: DynamicObject,
                    callback?: (response: ResponseModalObject) => void
                }>
            ) => {
                state.saveLoading = true;
            },
            prepare: (
                customWording: DynamicObject,
                callback?: (response: ResponseModalObject) => void
            ) => ({
                payload: {
                    customWording,
                    callback
                }
            })
        },
        saveCompanyCustomWordingResponseAction: (
            state
        ) => {
            state.saveLoading = false;
        },
        saveCustomSidebarItemsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    sidebarItems: SidebarItem[],
                    callback?: (response: ResponseModalObject) => void
                }>
            ) => {
                state.companyStudentMenuItemsLoading = true;
            },
            prepare: (
                sidebarItems: SidebarItem[],
                callback?: (response: ResponseModalObject) => void
            ) => ({
                payload: {
                    sidebarItems,
                    callback
                }
            })
        },
        saveCustomSidebarItemsResponseAction: (
            state,
            action: PayloadAction<SidebarItem[]>
        ) => {
            state.companyStudentMenuItemsLoading = false;
            state.companyStudentMenuItems = action.payload;
        },
        getQuestionAnswerConfigurationRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{ callback ?: (response: DynamicObject) => void }>
            ) => {
                state.questionAnswerLoading = true;
            },
            prepare: (
                callback?: (response: DynamicObject) => void
            ) => ({ payload: {callback} })
        },
        getQuestionAnswerConfigurationResponseAction: (
            state,
            action: PayloadAction<DynamicObject>
        ) => {
            state.questionAnswerLoading = false;
            state.questionAnswerItems = action.payload as QuestionAnswerItem[];
        },
        saveQuestionAnswerConfigurationRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{ 
                    payload: DynamicObject,
                    callback?: (response: ResponseModalObject) => void
                }>
            ) => {
                state.saveLoading = true;
            },
            prepare: (
                payload: DynamicObject,
                callback?: (response: ResponseModalObject) => void
            ) => ({
                payload: {
                    payload,
                    callback
                }
            })
        },
        saveQuestionAnswerConfigurationSuccessAction: (
            state
        ) => {
            state.saveLoading = false;
        }
	},
	extraReducers: (builder) => {
		builder
			.addCase('organisation/selectUserOrganisationSuccessAction', (state) => {
				state.selectedUserCompany = initialState.selectedUserCompany;
			})
	},
});

const { actions, reducer } = companySlice;

// Actions
export const {
	getUserCompaniesRequestAction,
	getUserCompaniesSuccessAction,
	selectUserCompanyRequestAction,
	selectUserCompanySuccessAction,
	saveUserCompanyRequestAction,
	saveUserCompanyResponseAction,
	uploadCompanyLogoRequestAction,
	getSystemConfigurationSuccessAction,
	getCompanyWorkflowOptionsRequestAction,
	getCompanyWorkflowOptionsSuccessAction,
	saveCompanyUserSettingsRequestAction,
	saveCompanyUserSettingsResponseAction,
	getCustomFieldsChangesRequestAction,
    getCompanyCustomWordingRequestAction,
    getCompanyCustomWordingResponseAction,
    saveCompanyCustomWordingRequestAction,
    saveCompanyCustomWordingResponseAction,
    saveCustomSidebarItemsRequestAction,
    saveCustomSidebarItemsResponseAction,
    getQuestionAnswerConfigurationRequestAction,
    getQuestionAnswerConfigurationResponseAction,
    saveQuestionAnswerConfigurationRequestAction,
    saveQuestionAnswerConfigurationSuccessAction
} = actions;

export const resetAllCompanyDataTableFiltersAction = createAction('company/resetAllCompanyDataTableFiltersAction');

// Selectors
export const getCompaniesState = (state: RootState) => state.companies;
export const getCompaniesLoading = createSelector(
	getCompaniesState,
	(companies: CompaniesState) => companies.loading
);
export const getSystemConfiguration = createSelector(
	getCompaniesState,
	(companies: CompaniesState) => companies.systemConfiguration
);
export const getWorkflowFetchLoading = createSelector(
	getCompaniesState,
	(companies: CompaniesState) => companies.workflowFetchLoading
);
export const getWorkflowOptions = createSelector(
	getCompaniesState,
	(companies: CompaniesState) => companies.workflowOptions
);
export const getFullWorkflowOptions = createSelector(
	getCompaniesState,
	(companies: CompaniesState) => companies.fullWorkflowOptions
);
export const getUserCompanies = createSelector(
	getCompaniesState,
	(companies: CompaniesState) => companies.userCompanies
);
export const getSelectedUserCompany = createSelector(
	getCompaniesState,
	(companies: CompaniesState) => companies.selectedUserCompany
);
export const getSelectedUserCompanyCustomerId = createSelector(
	getSelectedUserCompany,
	(selectedUserCompany: CompanyUserRole | undefined) => {
		return get(selectedUserCompany, 'User.CustomerId', undefined);
	}
);
export const getSelectedUserCompanyRole = createSelector(
	getSelectedUserCompany,
	(selectedUserCompany: CompanyUserRole | undefined) => {
		return get(selectedUserCompany, 'Role');
	}
);
export const getSelectedUserCompanyUserRoleName = createSelector(
	getSelectedUserCompanyRole,
	(selectedUserCompanyRole) => get(selectedUserCompanyRole, 'Name', '')
);
export const getSelectedCompany = createSelector(
	getSelectedUserCompany,
	(selectedUserCompany: CompanyUserRole | undefined) => {
		return get(selectedUserCompany, 'Company');
	}
);
export const getSelectedCompanyId = createSelector(
	getSelectedCompany,
	(selectedCompany) => {
		return get(selectedCompany, 'CompanyId', undefined);
	}
);
export const getCustomerUILabel = createSelector(
	getSelectedCompany,
	(company) => {
		return get(company, 'CustomerTitleLabel', 'customer') || 'customer'; // fallback if null, undefined, or '';
	}
);
export const getSupportCashAllocation = createSelector(
	getSelectedCompany,
	(company) => {
		return get(company, 'SupportCashAllocation', false);
	}
);
export const getUsingCustomBankFileProcessor = createSelector(
    getSelectedCompany,
    (company) => get(company, 'UsingCustomBankFileProcessor', false)
);
export const getNumberOfDaysBeforeRecentCommentTurnedOff = createSelector(
	getSelectedCompany,
	(company) => {
		return get(company, 'NumberOfDaysBeforeRecentCommentTurnedOff', DEFAULT_NUMBER_OF_DAYS_FOR_RECENT_COMMENT);
	}
);
export const getLanguagePack = createSelector(
	getSelectedCompany, 
	(company) => {
		return get(company, 'LanguagePack');
	}
);
export const getUsingCustomerWorflow = createSelector(
	getSelectedCompany,
	(company) => {
		return get(company, 'UsingCustomerWorkflow', false);
	}
);
export const getSupportSendNewInvoice = createSelector(
	getSelectedCompany,
	(company) => {
		return get(company, 'SupportSendNewInvoice', false);
	}
);
export const getPaymentPlanEnabled = createSelector(
	getSelectedCompany,
	(company) => {
		return get(company, 'CompanyPaymentPlan.IsEnabled', false);
	}
);
export const getSelectedUserCompanyUser = createSelector(
	getSelectedUserCompany,
	(selectedUserCompany: CompanyUserRole | undefined) => {
		return get(selectedUserCompany, 'User');
	}
);
export const getEnableBankFileImporting = createSelector(
	getSelectedCompany,
	(company) => {
		return get(company, 'EnableBankFileImporting', false);
	}
);
export const getCloudImportType = createSelector(
	getSelectedCompany,
	(company) => {
		return get(company, 'CloudImportType');
	}
);
export const getCompanyTimezone = createSelector(
	getSelectedCompany,
	(company) => {
		return get(company, 'Timezone');
	}
);
export const getCompanySaveLoading = createSelector(
    getCompaniesState,
    (companies: CompaniesState) => companies.saveLoading
);
export const getCompanyCustomWording = createSelector(
    getCompaniesState,
    (companies: CompaniesState) => companies.customWording
);
export const getCompanyWordingLoading = createSelector(
    getCompaniesState,
    (companies: CompaniesState) => companies.wordingLoading
);
export const getCompanyStudentMenuItemsLoading = createSelector(
    getCompaniesState,
    (companies: CompaniesState) => companies.companyStudentMenuItemsLoading
);
export const getCompanyStudentMenuItems = createSelector(
    getCompaniesState,
    (companies: CompaniesState) => companies.companyStudentMenuItems
);
export const getQuestionAnswerItems = createSelector(
    getCompaniesState,
    (companies: CompaniesState) => companies.questionAnswerItems
);
export const getQuestionAnswerLoading = createSelector(
    getCompaniesState,
    (companies: CompaniesState) => companies.questionAnswerLoading
);
export const getIsEnableCustomerPortal = createSelector(
    getSelectedCompany,
    (company: Company | undefined) => get(company, 'EnableCustomerPortal', false)
);

// Reducer
export default reducer;